import React, { Component, CSSProperties, ReactNode } from 'react'
import Downshift from 'downshift'
import compose from 'lodash/flowRight'
import styled, { css } from 'styled-components'

import { COLORS, SIZES } from '~/common/globalStyles'
import {
  SelectComponentWrapper,
  SelectDropdownButton,
  Placeholder,
  Menu,
} from '~/views/components/ui/inputs/v2/Select/shared/components/selectElements'
import { groupArray } from '~/views/components/ui/inputs/v2/shared/utils/selectHelpers'
import {
  ProviderProps,
  withDrawerLockingContext,
} from '~/views/components/WithDrawerLockingContext'

interface SelectDropdownInputProps {
  styledInputMode: boolean
  borderless: boolean
  transparentInput: boolean
}

const SelectDropdownInput = styled.input<SelectDropdownInputProps>`
  border: none;
  cursor: pointer;
  width: ${({ styledInputMode }) => (styledInputMode ? '100%' : '3px')};
  ${({ borderless }) =>
    borderless
      ? css`
          background: transparent;
          position: absolute;
          align-self: center;
          color: ${COLORS.blue};
        `
      : ''};

  ${({ transparentInput, styledInputMode }) =>
    transparentInput &&
    css`
      background: transparent;
      position: absolute;
      border: none;
      font-size: ${SIZES.large};
      width: ${styledInputMode ? '100%' : '20px'};
    `};

  &:focus {
    outline: 0;
  }
`

interface WrapperProps {
  styledInputMode: boolean
  focused: boolean
  isOpen: boolean
}

const Wrapper = styled.div<WrapperProps>`
  opacity: ${({ styledInputMode, focused, isOpen }) =>
    styledInputMode ? 0 : focused && isOpen ? 0.5 : 1};
  width: 100%;

  display: flex;
  align-items: center;

  div {
    padding: 0;
  }
`

interface SearchableSelectProps<Item> {
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  name?: string
  placeholder: string
  renderValue: ({ value }: { value: string[] }) => ReactNode
  required?: boolean
  id: string
  disabled?: boolean
  drawerLockingContext: ProviderProps
  noPadding?: boolean
  borderless?: boolean
  style?: CSSProperties
  value: string
  onChange?: (selectedItem: Item) => void
  menuPosition: 'top'
  menuWrapper?: boolean
  floating?: boolean
  fullWidth?: boolean

  filterMethod: (child: Item) => any
  grouped: boolean
  groupCriteria: (data: React.ReactElement) => any
  menuStyle: CSSProperties
  transparentInput: boolean
  placeholderColor?: string
  dropdownDataTestId?: string
  autoFocusInput?: boolean
  children: React.ReactNode
}

class SearchableSelect extends Component<
  SearchableSelectProps<HTMLDivElement>
> {
  inputMode = false
  highlightedIndex = 0
  childrenArray: React.ReactNode[] = []
  filteredChildrenArray: React.ReactNode[] = []
  override state = {
    focused: false,
  }
  static defaultProps: { filterMethod: (child: any) => any }
  searchInput: HTMLInputElement | null = null
  formInput: HTMLInputElement | null = null

  override componentDidUpdate() {
    this.childrenArray = React.Children.toArray(this.props.children)
    this.filteredChildrenArray = this.childrenArray
  }

  onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = this.props
    this.setState({ focused: true })
    if (onFocus) {
      onFocus(event)
    }
  }

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = this.props
    this.setState({ focused: false }, () => {
      if (this.searchInput) {
        this.searchInput.value = ''
      }
    })
    if (onBlur) {
      onBlur(event)
    }
  }

  onStateChange = (event: any) => {
    const { drawerLockingContext } = this.props
    if (event.isOpen) {
      drawerLockingContext && drawerLockingContext.disableBackdropClick()
    }
  }

  highlightSelectedItem(selectedItem: Record<string, string>) {
    this.highlightedIndex = 0
    this.childrenArray.forEach((child, index) => {
      if (
        selectedItem.value ===
        (child as React.ReactElement<{ value: string }>).props.value
      ) {
        this.highlightedIndex = index
      }
    })
  }

  override render() {
    const {
      children,
      placeholder,
      renderValue,
      fullWidth,
      required,
      id,
      disabled,
      value,
      onChange,
      menuPosition,
      filterMethod,
      drawerLockingContext,
      grouped,
      groupCriteria,
      borderless,
      noPadding,
      menuStyle,
      transparentInput,
      style,
      placeholderColor,
      dropdownDataTestId,
      autoFocusInput = false,
      ...rest
    } = this.props

    return (
      <Downshift
        itemToString={(selected) => (selected ? selected.name : '')}
        selectedItem={{ value: value || '' }}
        onChange={(selectedItem) => {
          this.inputMode = false
          this.searchInput?.blur()
          this.formInput?.focus()
          return onChange?.(selectedItem.value)
        }}
        onStateChange={this.onStateChange}
        {...rest}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          getRootProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
          openMenu,
          setHighlightedIndex,
        }) => {
          this.inputMode = !!inputValue
          this.highlightSelectedItem(selectedItem)
          const showPlaceholder = !selectedItem.value && placeholder
          return (
            <SelectComponentWrapper {...getRootProps({ refKey: 'innerRef' })}>
              <SelectDropdownButton
                data-testid={dropdownDataTestId || 'select-dropdown-button'}
                disabled={disabled}
                onFocus={this.onFocus}
                fullWidth={fullWidth}
                onBlur={this.onBlur}
                focused={this.state.focused}
                onClick={() => this.searchInput?.focus()}
                borderless={borderless}
                noPadding={noPadding}
                style={style}
              >
                <SelectDropdownInput
                  data-testid="search-user-input"
                  transparentInput={transparentInput}
                  borderless={borderless}
                  ref={(node) => {
                    this.searchInput = node
                  }}
                  styledInputMode={this.inputMode}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={autoFocusInput}
                  {...getInputProps({
                    onFocus: (event) => {
                      event.target.value = ''
                      setHighlightedIndex(this.highlightedIndex)
                      openMenu()
                    },
                    disabled,
                  })}
                />
                <Wrapper
                  styledInputMode={this.inputMode}
                  focused={this.state.focused}
                  isOpen={isOpen}
                >
                  {showPlaceholder ? (
                    <Placeholder color={placeholderColor}>
                      {placeholder}
                    </Placeholder>
                  ) : typeof renderValue === 'function' ? (
                    renderValue(selectedItem)
                  ) : (
                    React.Children.map(children, (child) =>
                      (child as React.ReactElement<{ value: string }>)?.props
                        .value === selectedItem.value
                        ? child
                        : null,
                    )?.filter(Boolean)
                  )}
                </Wrapper>
                <input
                  data-testid="dropdown-input"
                  id={id}
                  required={required}
                  value={selectedItem.value}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  onChange={() => false}
                  onKeyDown={(event) => {
                    drawerLockingContext &&
                      drawerLockingContext.handleKeyDown(
                        event as unknown as KeyboardEvent,
                      )
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  ref={(node) => {
                    this.formInput = node
                  }}
                />
              </SelectDropdownButton>
              {isOpen && (
                <div
                  style={borderless ? { width: 250, position: 'absolute' } : {}}
                >
                  <Menu
                    menuPosition={menuPosition}
                    menuStyle={menuStyle}
                    {...getMenuProps({ open: isOpen })}
                  >
                    {groupArray(
                      getItemProps,
                      inputValue,
                      highlightedIndex,
                      selectedItem,
                      grouped,
                      groupCriteria,
                      filterMethod,
                      this.childrenArray,
                      false,
                    )}
                  </Menu>
                </div>
              )}
            </SelectComponentWrapper>
          )
        }}
      </Downshift>
    )
  }
}

SearchableSelect.defaultProps = {
  filterMethod: (child: any) => child,
}

export default compose(withDrawerLockingContext)(SearchableSelect)
